import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, CircularProgress } from '@mui/material';
import ZohoChatbot from './Chatbot/ZohoChatbot';
import ChatIcon from '@mui/icons-material/Chat';

const ContactUs = () => {
  const chatbotRef = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [initAttempts, setInitAttempts] = useState(0);
  const maxAttempts = 3;

  // Add/remove data-page attribute when component mounts/unmounts
  useEffect(() => {
    document.body.setAttribute('data-page', 'support');
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, []);

  const handleChatClick = async () => {
    setIsLoading(true);
    
    try {
      // Wait for initialization if needed
      if (!chatbotRef.current?.isInitialized()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Try to open chat
      if (chatbotRef.current) {
        const success = chatbotRef.current.openChat();
        if (success) {
          setInitAttempts(0);
          setIsLoading(false);
          return;
        }
      }

      // If we get here, the attempt failed
      setInitAttempts(prev => {
        if (prev >= maxAttempts - 1) {
          throw new Error('Failed to open chat after multiple attempts');
        }
        return prev + 1;
      });

      // Wait before allowing another try
      setTimeout(() => setIsLoading(false), 1000);

    } catch (error) {
      console.error('Error opening chat:', error);
      setIsLoading(false);
      alert('Unable to open chat. Please try refreshing the page.');
    }
  };

  return (
    <Box sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={3} sx={{ maxWidth: 400, width: '100%', p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={1}>Contact Us</Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Need help? Our support team is here for you. Chat with us for instant assistance.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ChatIcon />}
          onClick={handleChatClick}
          disabled={isLoading}
          sx={{ 
            borderRadius: 8, 
            fontWeight: 600, 
            px: 4, 
            py: 1.5, 
            fontSize: '1.1rem', 
            boxShadow: 3, 
            minWidth: 180,
            position: 'relative',
            '&.Mui-disabled': {
              backgroundColor: 'primary.main',
              color: 'white',
              opacity: 0.8
            }
          }}
        >
          {isLoading ? "Opening Chat..." : "Chat Now"}
        </Button>
        {initAttempts > 0 && initAttempts < maxAttempts && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Chat is taking longer to load. Please wait...
          </Typography>
        )}
        <ZohoChatbot 
          ref={chatbotRef} 
          open={true} 
          onReady={() => {
            console.log('Zoho widget ready');
            setInitAttempts(0);
          }} 
        />
      </Paper>
    </Box>
  );
};

export default ContactUs;